// eslint-disable-next-line max-classes-per-file
import { isNil, without } from "lodash";
import { IMapDrawLayer, IMapGeometryWicket } from "~app/common/components/map/map.settings";
import { Subtyp1, Subtyp2, Subtyp3 } from "~app/common/components/map/symbols/kartgeometri.constants";
import { Symbols } from "~app/common/components/map/symbols/symbol.styles";
import { SkogsvardAtgardsGrupp } from "~app/common/models/constants";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Kartgeometri } from "~app/features/map/popups/kartgeometri-popup/kartgeometri.model";
import { Kartgeometrityper } from "~app/features/planera/services/karta.service";
import { KartgeometriVirkeskopDrivningspunktRemapper } from "../utils/KartgeometriVirkeskopDrivningspunktRemapper";
import { VKAtgardHelper } from "../utils/vkatgard-helper";
import { ICollectorAtgardViewModel, ICollectorDrivningspunktViewModel, ICollectorSubtyper } from "./atgard-collector-models";
import { Atgard } from "./atgard-models";

export class IMapGeometryLayer {
    mapGeometry: IMapGeometryWicket;
    geometri: Geometri;
    layer: IMapDrawLayer;
}

export class VKGeometrierDTO {
    kontraktId: number;
    kontraktGUID: string;
    atgardsgeometrier: VKAtgardGeometriDTO[];
    geometrier: VKGeometriDTO[];
}

export class VKAtgardGeometriDTO {
    beteckning: string;
    atgardsgrupp: string;
    atgardstyp: string;
    atgardsnamn?: string;
    atgardId?: number;
    atgardGUID: string;
    geometri: VKGeometriDTO;
    drivningspunkter: VKGeometriDTO[]; //leveransvirke
}

export class VKGeometriDTO {
    id?: number;
    guid: string;
    typ: string;
    wkt: string;
    subtyp1: string;
    subtyp2: string;
    subtyp3: string;
    anteckning: string;
    karttext?: string;
    tillganglighetsklass: string;
    hansynAtgardTyp: string;
    etikett: string;
    malklass: string;
    geometrityp: string;
    bredd?: number;
    subtyp: string;
    yta?: number;
    langd?: number;
}

export class Geometrier {

    kontraktGuid: string;
    atgardGeometrier: Geometri[];
    kontraktGeometrier: Geometri[];
    skogsvardGeometrier: Geometri[];

    constructor(geometrier: VKGeometrierDTO) {
        this.kontraktGuid = geometrier?.kontraktGUID;

        const skogsvardDTOGeometrier = geometrier?.atgardsgeometrier?.filter(x => VKAtgardHelper.isAtgardsGruppSkogsvard(x.atgardsgrupp));
        const atgardDTOGeometrier = without(geometrier?.atgardsgeometrier, ...skogsvardDTOGeometrier);

        this.skogsvardGeometrier = skogsvardDTOGeometrier?.map((g, i) => new Geometri("skogsvard", "", i+1, geometrier.kontraktGUID, g, g.geometri));

        this.atgardGeometrier = atgardDTOGeometrier?.map((g, i) => new Geometri("atgard", "", i+1, geometrier.kontraktGUID, g, g.geometri));

        this.kontraktGeometrier = geometrier?.geometrier?.map(g => {
            const dto = KartgeometriVirkeskopDrivningspunktRemapper.remapFromBackEndModelSingle(g);
            return new Geometri("kontrakt", g.subtyp2, 0, geometrier.kontraktGUID, null, dto);
        });
    }

}

export interface ISkogsvardVirtualLayer {
    group: string;
    active: boolean;
}

export type GeometriParentType = "" | "kontrakt" | "atgard" | "skogsvard" | "leveransvirke";

export class Geometri {

    parentType: GeometriParentType;
    kartgeometri: Kartgeometri;
    guid: string;
    kontraktGuid: string;
    atgardGuid?: string;
    atgardBeteckning?: string;
    atgardAtgardsgrupp?: string;
    atgardAtgardstyp?: string;
    atgardAtgardsnamn?: string;
    atgardDrivningspunkter?: Geometri[];
    mapGeometry: IMapGeometryWicket;
    areal?: number;
    length?: number;
    index: number;

    get isAvlaggInvalid(): boolean {
        if (!this.atgardDrivningspunkter) {
            return true;
        }
        const subtyper = [
            Subtyp1.drivningspunkt,
            Subtyp2.avlagg,
            null
        ];

        const kgeos = this.atgardDrivningspunkter
            .filter(geometri => geometri.isSameSubtyp(subtyper) ||
                geometri.isMixedSubtyp());
        return kgeos.length === 0 || kgeos.some(geo => !geo.kartgeometri.tillganglighetsklass);
    }

    get isAvlaggMissing(): boolean {
        if (!this.atgardDrivningspunkter) {
            return true;
        }
        const subtyper = [
            Subtyp1.drivningspunkt,
            Subtyp2.avlagg,
            null
        ];

        const kgeos = this.atgardDrivningspunkter
            .filter(geometri => geometri.isSameSubtyp(subtyper) ||
                geometri.isMixedSubtyp());
        return kgeos.length === 0;
    }

    get isLarmkoordinatInvalid(): boolean {
        if (!this.atgardDrivningspunkter) {
            return true;
        }
        const subtyper = [
            Subtyp1.drivningspunkt,
            Subtyp3.ovrigOverfartLarmpunkt,
            null
        ];

        const kgeos = this.atgardDrivningspunkter
            .filter(geometri => geometri.isSameSubtyp(subtyper) ||
                geometri.isMixedSubtyp());
        return kgeos.length !== 1;
    }

    get larmpunkter(): Geometri[] {
        if (!this.atgardDrivningspunkter) {
            return null;
        }
        const subtyper = [
            Subtyp1.drivningspunkt,
            Subtyp3.ovrigOverfartLarmpunkt,
            null
        ];

        return this.atgardDrivningspunkter
            .filter(geometri => geometri.isSameSubtyp(subtyper) ||
                geometri.isMixedSubtyp());
    }

    get avlagg(): Geometri[] {
        if (!this.atgardDrivningspunkter) {
            return null;
        }
        const subtyper = [
            Subtyp1.drivningspunkt,
            Subtyp2.avlagg,
            null
        ];

        return this.atgardDrivningspunkter
            .filter(geometri => geometri.isSameSubtyp(subtyper) ||
                geometri.isMixedSubtyp());
    }

    get isHansynsyta(): boolean {
        return this.kartgeometri?.subtyp1 === Kartgeometrityper.HansynYta;
    }

    get isLarmpunkt(): boolean {
        return this.kartgeometri.subtyp1 === Subtyp1.drivningspunkt &&
            this.kartgeometri.subtyp2 === Subtyp2.ovrigOverfartLarmpunkt;
    }

    get isKojplats(): boolean {
        return this.kartgeometri.subtyp1 === Subtyp1.drivningspunkt &&
            this.kartgeometri.subtyp2 === Subtyp3.ovrigOverfartKojplats;
    }

    get isAvlagg(): boolean {
        return this.kartgeometri.subtyp1 === Subtyp1.drivningspunkt &&
            this.kartgeometri.subtyp2 === Subtyp2.avlagg;
    }

    constructor(parent: GeometriParentType,
        label: string,
        index: number,
        kontraktGuid?: string,
        atgDto?: VKAtgardGeometriDTO,
        geometriDto?: VKGeometriDTO) {

        const kartgeometri = this.mapToKartgeometri(geometriDto);
        const styleName: keyof typeof Symbols.Styles = (parent === "atgard" ? "atgardYta" : null);

        this.parentType = parent;
        this.index = index;
        this.kartgeometri = kartgeometri;
        this.mapGeometry = {
            id: geometriDto?.guid,
            label: label,
            wkt: geometriDto?.wkt,
            tag: kartgeometri,
            styleName: styleName,
            visible: true
        };
        this.guid = geometriDto?.guid ?? atgDto?.atgardGUID;
        this.kontraktGuid = kontraktGuid;
        this.atgardGuid = atgDto?.atgardGUID;
        this.atgardBeteckning = atgDto?.beteckning;
        this.atgardAtgardstyp = atgDto?.atgardstyp;
        this.atgardAtgardsnamn = atgDto?.atgardsnamn;
        this.atgardAtgardsgrupp = atgDto?.atgardsgrupp;
        this.atgardDrivningspunkter = this.mapAtgardDrivningspunkter(kontraktGuid, atgDto);
        this.areal = geometriDto?.yta;
    }

    updateFromAtgard(atgard: Atgard) {
        this.atgardBeteckning = atgard.beteckning;
        this.atgardAtgardsgrupp = atgard.atgardsgrupp;
        this.atgardAtgardstyp = atgard.atgardstyp;
        this.atgardAtgardsnamn = atgard.atgardsnamn;

        if (ObjectUtils.hasValue(this.mapGeometry)) {
            this.mapGeometry.tag = atgard;
            if (this.atgardAtgardsgrupp === SkogsvardAtgardsGrupp.markberedning) {
                this.mapGeometry.styleName = "skogsvardMarkberedningYta";
            } else if (this.atgardAtgardsgrupp === SkogsvardAtgardsGrupp.foryngring) {
                this.mapGeometry.styleName = "skogsvardPlanteringYta";
            } else if (this.atgardAtgardsgrupp === SkogsvardAtgardsGrupp.rojning) {
                this.mapGeometry.styleName = "skogsvardRojningYta";
            } else if (this.atgardAtgardsgrupp === SkogsvardAtgardsGrupp.inventering) {
                this.mapGeometry.styleName = "skogsvardInventeringYta";
            } else if (this.atgardAtgardsgrupp === SkogsvardAtgardsGrupp.ovrigt) {
                this.mapGeometry.styleName = "skogsvardOvrigYta";
            }
            this.updateLabel();
        }
    }

    isSameSubtyp([
        subtyp1,
        subtyp2,
        subtyp3]: string[
]): boolean {
        return this.kartgeometri.subtyp1 === subtyp1 &&
            this.kartgeometri.subtyp2 === subtyp2 &&
            this.kartgeometri.subtyp3 === subtyp3;
    }

    isMixedSubtyp(): boolean {
        return this.kartgeometri.subtyp2 === Subtyp2.avlaggLarmpunkt;
    }

    setNewGeometri(mapGeometry: IMapGeometryWicket, kartgeometri?: Kartgeometri): void {
        this.mapGeometry = mapGeometry;
        this.mapGeometry.id = this.guid;
        this.mapGeometry.styleName = this.parentType === "atgard" ? "atgardYta" : null;
        this.mapGeometry.isNew = true;
        this.mapGeometry.visible = true;
        this.mapGeometry.label = `${this.atgardBeteckning} ${this.atgardAtgardstyp}`;
        this.kartgeometri = !isNil(kartgeometri) ? kartgeometri : new Kartgeometri(null, null, null, null, null, true, null, null, null);
        this.kartgeometri.geometri = this.mapGeometry.wkt;
        this.mapGeometry.tag = this.kartgeometri;
    }

    addAtgardDrivningspunkt(drivningspunkt: Geometri) {
        if (!this.atgardDrivningspunkter.some(p => p.guid === drivningspunkt.guid)) {
            this.atgardDrivningspunkter.push(drivningspunkt);
        }
    }

    private generateGeometriLabel(): string {
        if (this.parentType === "skogsvard" || this.parentType === "atgard") {
            return `${this.index} - ${this.atgardBeteckning} ${this.atgardAtgardstyp}`;
        }
        return this.mapGeometry.label;
    }

    private updateLabel() {
        if (this.mapGeometry) {
            this.mapGeometry.label = this.generateGeometriLabel();
        }
    }

    private mapToKartgeometri(geometriDto: VKGeometriDTO): Kartgeometri {
        const kartgeometri = new Kartgeometri(
            geometriDto?.subtyp1,
            geometriDto?.subtyp2,
            geometriDto?.subtyp3,
            geometriDto?.anteckning,
            geometriDto?.wkt,
            false,
            geometriDto?.tillganglighetsklass,
            geometriDto?.hansynAtgardTyp,
            geometriDto?.karttext,
            geometriDto?.etikett);
        kartgeometri.langd = geometriDto?.langd;
        kartgeometri.guid = geometriDto?.guid;
        return kartgeometri;
    }

    private mapAtgardDrivningspunkter(kontraktGuid: string, vkatgardGeometri: VKAtgardGeometriDTO): Geometri[] {
        if (isNil(vkatgardGeometri?.drivningspunkter)) {
            return [];
        }
        const result = [];
        vkatgardGeometri.drivningspunkter.forEach(punkt => {
            const dto = KartgeometriVirkeskopDrivningspunktRemapper.remapFromBackEndModelSingle(punkt);
            const geo = new Geometri("", dto.subtyp2, 0, kontraktGuid, null, punkt);
            geo.atgardGuid = vkatgardGeometri.atgardGUID;
            result.push(geo);
        });

        return result;
    }
}

export interface ICollectorImported {
    kontraktgeometrier: ICollectorGeometriWrapper[];
    atgardgeometrier: ICollectorAtgardViewModel[];
    drivningspunktgeometrier: ICollectorDrivningspunktViewModel[];
    updatedgeometrier: ICollectorSubtyper[];
    continuecollector: boolean;
}

export interface ICollectorGeometriWrapper {
    typ: string;
    geometri: ICollectorSubtyper;
    selected: boolean;
    existsInKontrakt: boolean;
}

export interface IHoverMapObject {
    guid: string[];
    isOver: boolean;
}

export class GeometryUpdate {
    layerId: string;
    kontraktGuid: string;
    atgardGuid?: string;
    geometryId?: string;
    wkt: string;
    metadata?: any;
    constructor(layerid: string, kontraktGuid: string, wkt: string, atgardGuid?: string, geometryId?: string, metadata?: any) {
        this.layerId = layerid;
        this.kontraktGuid = kontraktGuid;
        this.atgardGuid = atgardGuid;
        this.geometryId = geometryId;
        this.wkt = wkt;
        this.metadata = metadata;
    }
}
